import type { SearchResultDoc } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { DocChatConversation } from 'features/knowledgeDocumentViewer/types'
import { unscopeSearchDocumentIdForHydration } from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'

function addUnscopedDocumentId(
  ids: Set<string>,
  documentId: string,
  pipelineConfigId?: string
) {
  const unscoped = unscopeSearchDocumentIdForHydration(
    documentId,
    pipelineConfigId
  )
  if (unscoped.length > 0) ids.add(unscoped)
}

function addResultSnapshotDocumentIds(
  ids: Set<string>,
  result: SearchResultDoc,
  pipelineConfigId?: string
) {
  addUnscopedDocumentId(ids, result.document_id, pipelineConfigId)
  for (const group of result.drawing_revision_groups ?? []) {
    for (const option of group.options) {
      addUnscopedDocumentId(ids, option.document_id, pipelineConfigId)
    }
  }
}

export function collectConversationDocumentIds(
  conversation: DocChatConversation | undefined,
  pipelineConfigId?: string
): string[] {
  if (!conversation?.turns.length) return []

  const ids = new Set<string>()
  for (const turn of conversation.turns) {
    const events = turn.pendingResponse
      ? [...turn.events, turn.pendingResponse]
      : turn.events
    for (const event of events) {
      if (event.type !== 'response' && event.type !== 'response_partial') {
        continue
      }

      for (const result of event.results ?? []) {
        addResultSnapshotDocumentIds(ids, result, pipelineConfigId)
      }
      if (event.type === 'response') {
        for (const revision of event.revision_results ?? []) {
          if (revision.kind === 'diff') {
            addUnscopedDocumentId(
              ids,
              revision.from_document_id,
              pipelineConfigId
            )
            addUnscopedDocumentId(
              ids,
              revision.to_document_id,
              pipelineConfigId
            )
          } else if (revision.jump_ref) {
            addUnscopedDocumentId(
              ids,
              revision.jump_ref.document_id,
              pipelineConfigId
            )
          }
        }
      }
      for (const segment of event.response_segments ?? []) {
        for (const citation of segment.citations) {
          if (citation.type !== 'page_text') continue

          addUnscopedDocumentId(ids, citation.document_id, pipelineConfigId)
        }
      }
    }
  }
  return Array.from(ids)
}
